import React from 'react';
import { DocsLayout } from '../components/DocsLayout';
import { MarkdownContent } from '../components/MarkdownContent';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  User,
  Bell,
  Palette,
  Mail,
  MessageSquare,
  Smartphone,
  Clock,
  Lightbulb,
  AlertCircle
} from 'lucide-react';

export const ProfileSettingsGuidePage: React.FC = () => {
  return (
    <DocsLayout
      title='Profile & Settings'
      description='Customize your account and application preferences'
      breadcrumbs={[
        { label: 'Documentation', path: '/docs' },
        { label: 'Features', path: '/docs/features' },
        { label: 'Profile & Settings' }
      ]}
    >
      <MarkdownContent>
        <div className='max-w-[1000px]'>
          <div className='not-prose mb-6'>
            <Card className='border-primary/50 bg-primary/5'>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <User className='h-8 w-8 text-primary' />
                  Profile & Settings Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6'>
                  Manage your personal information, notification preferences, and application
                  settings. Customize Vehix to match your workflow and communication preferences.
                </p>
              </CardContent>
            </Card>
          </div>

          <section className='mb-20'>
            <h2 className='text-3xl font-bold mb-6'>Accessing Profile & Settings</h2>
            <p className='text-sm leading-6'>Click your avatar in the sidebar to access:</p>
            <ul className='text-sm leading-6'>
              <li>
                <strong>View Profile:</strong> See your complete profile information
              </li>
              <li>
                <strong>Edit Profile:</strong> Update your personal details
              </li>
              <li>
                <strong>Notification Preferences:</strong> Configure alerts and communications
              </li>
              <li>
                <strong>Appearance:</strong> Choose light/dark theme
              </li>
              <li>
                <strong>Sign Out:</strong> Log out of your account
              </li>
            </ul>
          </section>

          <section className='mb-20'>
            <h2 className='text-3xl font-bold mb-6'>Your Profile Information</h2>
            <div className='not-prose my-6'>
              <Card>
                <CardHeader>
                  <CardTitle className='text-lg font-semibold'>Profile Details</CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                  <div>
                    <h4 className='font-semibold mb-2'>Your profile displays:</h4>
                    <ul className='space-y-1 text-sm leading-6'>
                      <li>
                        <strong>Basic Info:</strong> Name, email, phone number, job title
                      </li>
                      <li>
                        <strong>Role & Permissions:</strong> Your assigned role and access level
                      </li>
                      <li>
                        <strong>Organization:</strong> Your dealership or dealer group
                      </li>
                      <li>
                        <strong>Account Status:</strong> Active, pending, or other status
                      </li>
                      <li>
                        <strong>Join Date:</strong> When your account was created
                      </li>
                      <li>
                        <strong>Last Login:</strong> Your most recent sign-in time
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          <section className='mb-20'>
            <h2 className='text-3xl font-bold mb-6'>Editing Your Profile</h2>
            <div className='not-prose my-6'>
              <Card>
                <CardHeader>
                  <CardTitle className='text-lg font-semibold'>Editable Fields</CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                  <div>
                    <h4 className='font-semibold mb-2'>You can update:</h4>
                    <ul className='space-y-1 text-sm leading-6'>
                      <li>
                        <strong>Phone Number:</strong> For SMS notifications and contact
                      </li>
                      <li>
                        <strong>Job Title:</strong> Your current position
                      </li>
                      <li>
                        <strong>Profile Photo:</strong> Upload or update your avatar
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className='not-prose my-6'>
              <Card className='border-yellow-500/50 bg-yellow-500/5'>
                <CardHeader>
                  <CardTitle className='flex items-center gap-2 text-yellow-700 dark:text-yellow-400'>
                    <AlertCircle className='h-5 w-5' />
                    Note
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6'>
                    Some fields like email and role can only be changed by administrators.
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          <section className='mb-20'>
            <h2 className='text-3xl font-bold mb-6'>Notification Preferences</h2>

            <div className='not-prose my-6'>
              <div className='grid gap-4 md:grid-cols-3'>
                <Card>
                  <CardHeader>
                    <div className='flex items-center gap-2'>
                      <Mail className='h-8 w-8 text-primary' />
                      <CardTitle className='text-lg font-semibold'>Email Notifications</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className='text-sm space-y-2'>
                    <ul className='space-y-1 leading-6'>
                      <li>
                        <strong>New Assignment:</strong> When recalls are assigned to you
                      </li>
                      <li>
                        <strong>Assignment Updates:</strong> When your assignments change
                      </li>
                      <li>
                        <strong>Route Created:</strong> When a new route is created for you
                      </li>
                      <li>
                        <strong>Campaign Priority:</strong> When high-priority campaigns are
                        announced
                      </li>
                      <li>
                        <strong>System Updates:</strong> Important platform announcements
                      </li>
                      <li>
                        <strong>Weekly Summary:</strong> Digest of your activity and completions
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <div className='flex items-center gap-2'>
                      <Smartphone className='h-8 w-8 text-primary' />
                      <CardTitle className='text-lg font-semibold'>SMS Notifications</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className='text-sm space-y-2'>
                    <p className='text-muted-foreground mb-2'>If Enabled:</p>
                    <ul className='space-y-1 leading-6'>
                      <li>
                        <strong>Urgent Assignments:</strong> High-priority recalls assigned to you
                      </li>
                      <li>
                        <strong>Route Changes:</strong> Changes to your active routes
                      </li>
                      <li>
                        <strong>Deadline Reminders:</strong> Upcoming recall deadlines
                      </li>
                      <li>
                        <strong>Customer Updates:</strong> Customer communication requiring response
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <div className='flex items-center gap-2'>
                      <Bell className='h-8 w-8 text-primary' />
                      <CardTitle className='text-lg font-semibold'>In-App Notifications</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className='text-sm space-y-2'>
                    <ul className='space-y-1 leading-6'>
                      <li>
                        <strong>New Messages:</strong> Team communications and updates
                      </li>
                      <li>
                        <strong>Status Changes:</strong> Recall or route status updates
                      </li>
                      <li>
                        <strong>Mentions:</strong> When someone mentions you in notes or comments
                      </li>
                      <li>
                        <strong>Completions:</strong> Confirmation of completed work
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          <section className='mb-20'>
            <h2 className='text-3xl font-bold mb-6'>Notification Settings</h2>

            <div className='not-prose my-6'>
              <div className='grid gap-4 md:grid-cols-2'>
                <Card>
                  <CardHeader>
                    <div className='flex items-center gap-2'>
                      <Clock className='h-8 w-8 text-primary' />
                      <CardTitle className='text-lg font-semibold'>Quiet Hours</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className='text-sm space-y-2'>
                    <p className='leading-6 mb-2'>
                      Set times when you don't want to receive notifications:
                    </p>
                    <ul className='space-y-1 leading-6'>
                      <li>Configure start and end times (e.g., 10 PM - 7 AM)</li>
                      <li>Urgent notifications may still come through</li>
                      <li>Useful for work-life balance</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <div className='flex items-center gap-2'>
                      <MessageSquare className='h-8 w-8 text-primary' />
                      <CardTitle className='text-lg font-semibold'>Frequency Settings</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className='text-sm space-y-2'>
                    <ul className='space-y-1 leading-6'>
                      <li>
                        <strong>Real-time:</strong> Immediate notification for each event
                      </li>
                      <li>
                        <strong>Batched:</strong> Grouped notifications sent hourly or daily
                      </li>
                      <li>
                        <strong>Off:</strong> Disable specific notification types
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          <section className='mb-20'>
            <h2 className='text-3xl font-bold mb-6'>Appearance Settings</h2>
            <div className='not-prose my-6'>
              <Card>
                <CardHeader>
                  <div className='flex items-center gap-2'>
                    <Palette className='h-8 w-8 text-primary' />
                    <CardTitle className='text-lg font-semibold'>Theme Options</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className='text-sm space-y-2'>
                  <p className='leading-6 mb-2'>Choose your preferred color theme:</p>
                  <ul className='space-y-1 leading-6'>
                    <li>
                      <strong>Light Mode:</strong> Traditional light background
                    </li>
                    <li>
                      <strong>Dark Mode:</strong> Reduced eye strain in low light
                    </li>
                    <li>
                      <strong>System:</strong> Match your device's theme setting
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </section>

          <section className='mb-20'>
            <h2 className='text-3xl font-bold mb-6'>Profile Completion</h2>
            <div className='not-prose my-6'>
              <Card className='border-green-500/50 bg-green-500/5'>
                <CardHeader>
                  <CardTitle className='text-green-700 dark:text-green-400'>
                    Complete Your Profile
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='text-sm leading-6 mb-3'>A complete profile helps your team:</p>
                  <ul className='space-y-1 text-sm leading-6'>
                    <li>Contact you effectively</li>
                    <li>Assign appropriate work</li>
                    <li>Understand your availability</li>
                    <li>Route communications correctly</li>
                  </ul>
                  <p className='text-sm leading-6 mt-3'>
                    Aim for 100% profile completion by filling in all optional fields.
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          <section className='mb-20'>
            <h2 className='text-3xl font-bold mb-6'>Tips for Settings</h2>
            <div className='not-prose my-6'>
              <Card>
                <CardHeader>
                  <div className='flex items-center gap-2'>
                    <Lightbulb className='h-8 w-8 text-primary' />
                    <CardTitle className='text-lg font-semibold'>Best Practices</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className='text-sm space-y-2'>
                  <ul className='space-y-1 leading-6'>
                    <li>
                      <strong>Enable key notifications:</strong> At minimum, enable new assignments
                      and urgent updates
                    </li>
                    <li>
                      <strong>Set realistic quiet hours:</strong> Balance availability with personal
                      time
                    </li>
                    <li>
                      <strong>Keep contact info current:</strong> Ensure your phone/email are up to
                      date
                    </li>
                    <li>
                      <strong>Try dark mode:</strong> Many users find it more comfortable for
                      extended use
                    </li>
                    <li>
                      <strong>Review periodically:</strong> Settings that worked initially may need
                      adjustment
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </section>
        </div>
      </MarkdownContent>
    </DocsLayout>
  );
};
